import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAccessKey,
  TechnologyApplication,
  TechnologyIntegration,
  TechnologyIntegrationUpsertPayload,
  TechnologySecret,
  createTechnologyIntegration,
  deleteTechnologyIntegration,
  listTechnologyAccessKeys,
  listTechnologyApplications,
  listTechnologyIntegrations,
  listTechnologySecrets,
  updateTechnologyIntegration,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_INTEGRATION_AUTH_OPTIONS,
  TECHNOLOGY_INTEGRATION_KIND_OPTIONS,
  TECHNOLOGY_INTEGRATION_STATUS_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

const emptyForm: TechnologyIntegrationUpsertPayload = {
  name: '',
  vendor: '',
  kind: 'Other',
  authKind: 'ApiKey',
  endpoint: '',
  scope: '',
  rateLimit: '',
  sla: '',
  status: 'Active',
  documentationUrl: '',
  applicationId: '',
  secretId: '',
  accessKeyId: '',
  notes: '',
};

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

export function TechnologyIntegrationsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.integrations');
  const [items, setItems] = useState<TechnologyIntegration[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyIntegrationUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [applications, setApplications] = useState<TechnologyApplication[]>([]);
  const [secrets, setSecrets] = useState<TechnologySecret[]>([]);
  const [keys, setKeys] = useState<TechnologyAccessKey[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyIntegrations({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar integrações.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  useEffect(() => {
    void listTechnologyApplications({ page: 1, pageSize: 200 })
      .then((result) => setApplications(result.items))
      .catch(() => setApplications([]));
    void listTechnologySecrets({ page: 1, pageSize: 200 })
      .then((result) => setSecrets(result.items))
      .catch(() => setSecrets([]));
    void listTechnologyAccessKeys({ page: 1, pageSize: 200 })
      .then((result) => setKeys(result.items))
      .catch(() => setKeys([]));
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyIntegration): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      vendor: item.vendor,
      kind: item.kind,
      authKind: item.authKind,
      endpoint: item.endpoint ?? '',
      scope: item.scope ?? '',
      rateLimit: item.rateLimit ?? '',
      sla: item.sla ?? '',
      status: item.status,
      documentationUrl: item.documentationUrl ?? '',
      applicationId: item.applicationId,
      secretId: item.secretId ?? '',
      accessKeyId: item.accessKeyId ?? '',
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyIntegrationUpsertPayload = {
      ...form,
      endpoint: form.endpoint?.trim() || null,
      scope: form.scope?.trim() || null,
      rateLimit: form.rateLimit?.trim() || null,
      sla: form.sla?.trim() || null,
      documentationUrl: form.documentationUrl?.trim() || null,
      secretId: form.secretId || null,
      accessKeyId: form.accessKeyId || null,
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyIntegration(editingId, payload);
        toast.success('Integração atualizada.');
        collapseForm(resetForm);
      } else {
        await createTechnologyIntegration(payload);
        toast.success('Integração criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar integração.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyIntegration): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir integração',
      message: `Deseja excluir "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyIntegration(item.id);
      toast.success('Integração excluída.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir integração.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Integrações"
        description="Conectores externos de cada aplicação, com autenticação, limite, SLA e a credencial do cofre."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar integração' : 'Nova integração'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingId ? `Editando ${form.name || 'integração'}` : undefined}
          createSummary="Clique para expandir e cadastrar uma integração"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Fornecedor
                <input
                  className="erp-input"
                  value={form.vendor}
                  onChange={(event) => setForm({ ...form, vendor: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  {TECHNOLOGY_INTEGRATION_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Aplicação
                <select
                  className="erp-input"
                  value={form.applicationId}
                  onChange={(event) => setForm({ ...form, applicationId: event.target.value })}
                  required
                >
                  <option value="">Selecione</option>
                  {applications.map((application) => (
                    <option key={application.id} value={application.id}>
                      {application.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Autenticação
                <select
                  className="erp-input"
                  value={form.authKind}
                  onChange={(event) => setForm({ ...form, authKind: event.target.value })}
                >
                  {TECHNOLOGY_INTEGRATION_AUTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_INTEGRATION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Credencial
                <select
                  className="erp-input"
                  value={form.secretId ?? ''}
                  onChange={(event) => setForm({ ...form, secretId: event.target.value })}
                >
                  <option value="">Nenhuma</option>
                  {secrets.map((secret) => (
                    <option key={secret.id} value={secret.id}>
                      {secret.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Chave
                <select
                  className="erp-input"
                  value={form.accessKeyId ?? ''}
                  onChange={(event) => setForm({ ...form, accessKeyId: event.target.value })}
                >
                  <option value="">Nenhuma</option>
                  {keys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Endpoint
                <input
                  className="erp-input"
                  value={form.endpoint ?? ''}
                  onChange={(event) => setForm({ ...form, endpoint: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Escopo
                <input
                  className="erp-input"
                  value={form.scope ?? ''}
                  onChange={(event) => setForm({ ...form, scope: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Limite
                <input
                  className="erp-input"
                  value={form.rateLimit ?? ''}
                  onChange={(event) => setForm({ ...form, rateLimit: event.target.value })}
                  placeholder="Ex.: 1000 req/min"
                />
              </label>
              <label className="erp-label">
                SLA
                <input
                  className="erp-input"
                  value={form.sla ?? ''}
                  onChange={(event) => setForm({ ...form, sla: event.target.value })}
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Documentação
              <input
                className="erp-input"
                value={form.documentationUrl ?? ''}
                onChange={(event) => setForm({ ...form, documentationUrl: event.target.value })}
              />
            </label>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome, fornecedor ou aplicação"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={statusFilter}
          onChange={(event) => {
            setPage(1);
            setStatusFilter(event.target.value);
          }}
        >
          <option value="">Todos os status</option>
          {TECHNOLOGY_INTEGRATION_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Fornecedor</th>
                  <th>Aplicação</th>
                  <th>Autenticação</th>
                  <th>Status</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 6 : 5} className="text-center text-secondary-500">
                      Nenhuma integração cadastrada.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.vendor}</td>
                      <td>{item.applicationName || '—'}</td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_INTEGRATION_AUTH_OPTIONS, item.authKind)}
                      </td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_INTEGRATION_STATUS_OPTIONS, item.status)}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
